// src/components/Home.jsx
import { useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify'; // Import toast
import { API_URL } from '../creds';
import { templateOptions } from '../data/index';

// Example templateOptions (English)
const templateLanguage = [
  { value: "en", label: "English" },
  { value: "hi", label: "Hindi" },
  { value: "ur", label: "Urdu" },
  { value: "te", label: "Telugu" },
].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

const Home = ({ setTemplate }) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    language: "en",
    template: 'en_EN', 
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.name.trim()) {
      toast.error('Project name is required!', {
        position: 'top-right',
        autoClose: 4000,
      });
      setLoading(false);
      return;
    }

    const payload = {
      name: formData.name.trim(),
      language: formData.language.trim(),
      template: formData.template.trim(),
    };

    try {
      const response = await fetch(`${API_URL}/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Uncomment when you have authentication
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to create project');
      }

      const newProject = await response.json();
      console.log('Created project:', newProject); // debug

      const projectId = newProject.projectid || newProject.project_id || newProject.slug;
      

      if (!projectId) {
        console.error('API response missing project ID/slug:', newProject);
        toast.error('Project created but navigation failed');
        return;
      }

      navigate(`/editor/${projectId}`, {
        state: { project: newProject }
      });

      // Close modal and reset form
      setIsModalOpen(false);
      setFormData({
        name: '',
        description: '',
        category: 'general',
      });
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.', {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="bg-gray-50 px-4 mt-20 md:px-6 lg:px-8">
        <div className="flex justify-start mb-12">
          <button
            onClick={() => setIsModalOpen(true)}
            className="group flex items-center gap-3 rounded-xl bg-blue-600 px-4 py-4 text-xl font-semibold text-white shadow-lg transition-all hover:bg-blue-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            disabled={loading}
          >
            <FaPlus className="h-7 w-7 transition-transform group-hover:rotate-90" />
            Create New Project
          </button>
        </div>

        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Markdown Book Editor
            </h1>
            <p className="mt-4 text-lg text-gray-600">
              Create and edit your markdown-based books and projects
            </p>
          </div>

          {/* Create Project Modal */}
          {isModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
              <div className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                {/* Modal Header */}
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-5">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Create New Book Project
                  </h2>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit} className="px-6 py-6">
                  <div className="space-y-5">
                    {/* Project Name */}
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Project Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        placeholder="Enter project name..."
                      />
                    </div>

                    {/* Template Dropdown */}
                    <div>
                  <label className="block text-sm font-medium text-gray-700">Choose Language</label>
                  <select
                    name="language"
                    value={formData.language}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-blue-500 focus:ring-blue-500"
                  >
                    {templateLanguage.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Start with Template</label>
                  <select
                    name="template"
                    value={formData.template}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-blue-500 focus:ring-blue-500"
                  >
                    {templateOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                  </div>

                  {/* Modal Footer */}
                  <div className="mt-8 flex justify-end gap-4 border-t border-gray-200 pt-6">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Creating...' : 'Create & Open Editor'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Home;